import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { ACCOUNT_INITIALIZE, LOGOUT } from '../../store/actions';
import API from '../../api/config/api';
import Loader from '../../ui-component/Loader';

//-----------------------|| AUTH GUARD ||-----------------------//

/**
 * Authentication guard for routes
 * @param {PropTypes.node} children children element/node
 */
const AuthGuard = ({ children }) => {
    const dispatch = useDispatch();
    const account = useSelector((state) => state.account);
    const { isLoggedIn, isInitialized, token } = account;
    const [isVerifying, setIsVerifying] = useState(false);

    // Use a ref to persist verification state across re-renders
    const hasVerifiedTokenRef = useRef(false);

    useEffect(() => {
        const initializeAuth = async () => {
            // Only verify token once per app session
            if (hasVerifiedTokenRef.current) return;

            // Always verify token on app load, regardless of isInitialized
            // This ensures token validity even if persisted state says we're logged in
            if (!token) {
                dispatch({
                    type: ACCOUNT_INITIALIZE,
                    payload: {
                        isLoggedIn: false,
                        user: null,
                        token: '',
                        role: null
                    }
                });
                hasVerifiedTokenRef.current = true;
                return;
            }

            // Token exists, verify it with /me endpoint
            setIsVerifying(true);
            try {
                const api = new API();
                const adminData = await api.verifyToken();

                // Token is valid, update store with admin data
                dispatch({
                    type: ACCOUNT_INITIALIZE,
                    payload: {
                        isLoggedIn: true,
                        user: adminData,
                        token: token,
                        role: adminData.role
                    }
                });
            } catch (error) {
                // Token verification failed, logout user
                dispatch({ type: LOGOUT });
            } finally {
                setIsVerifying(false);
                hasVerifiedTokenRef.current = true;
            }
        };

        initializeAuth();
    }, []); // Empty dependency array - only run once on mount

    // Show loader while verifying token
    if (isVerifying || !isInitialized) {
        return <Loader />;
    }

    // If not logged in after initialization, redirect to login
    if (!isLoggedIn) {
        return <Redirect to="/login" />;
    }

    return children;
};

AuthGuard.propTypes = {
    children: PropTypes.node
};

export default AuthGuard;
